import { Box, Table, TableContainer, Tbody, Th, Thead, Tr } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getDashboardBlocks } from 'redux/DashboardSliceV2';

import VarianceRow from './VarianceRows';

export interface VarianceProperties {
  output: any;
  columnSettings: any;
  columnPositions?: any;
}

const Variance = ({ output, columnSettings, columnPositions }: VarianceProperties) => {
  const [columns, setColumns] = useState<any>([]);
  const [rows, setRows] = useState<any>([]);
  const blockDetails = useSelector(getDashboardBlocks);

  const getDimensionNameFromId = (
    dimensionId: number | string,
    blockId: number | string,
    indicatorId: number | string,
  ): string | null => {
    if (!dimensionId || !blockId || !indicatorId || !Array.isArray(blockDetails)) {
      return null;
    }

    const foundBlock = blockDetails.find((block: any) => block.block_id === Number(blockId));
    if (!foundBlock) {
      return null;
    }

    const foundIndicator = foundBlock?.indicators?.find(
      (ind: any) => ind.indicator_id === Number(indicatorId),
    );
    if (!foundIndicator) {
      return null;
    }

    const foundDimension = foundIndicator?.indicator_dimensions?.find(
      (dim: any) => dim.indicator_dimension_id === Number(dimensionId),
    );

    return foundDimension?.indicator_dimension_name || null;
  };

  const extractColumns = (settings: any): string[] => {
    // Build the list of columns in the order they are enabled in settings
    const columnList: string[] = ['Indicators'];
    if (settings?.actual_column_name) columnList.push(settings?.actual_column_name);
    else columnList.push('Actual');
    if (settings?.previous_year) columnList.push('Prior Year');
    if (settings?.varience_to_py) columnList.push('▲PY');
    if (settings?.varience_percentage_py) columnList.push('▲PY%');
    if (settings?.plan_comparison_column_name)
      columnList.push(settings?.plan_comparison_column_name);
    else columnList.push('Comparison (PL)');
    // if (settings?.plan) columnList.push('Plan');
    if (settings?.varience_to_pl) columnList.push('▲PL');
    if (settings?.varience_percentage_pl) columnList.push('▲PL%');

    const colPos: Record<string, number> | undefined = columnPositions;
    if (colPos && typeof colPos === 'object') {
      const [first, ...rest] = columnList;
      const sortedRest = rest.slice().sort((a, b) => {
        const posA = colPos[a] || 999;
        const posB = colPos[b] || 999;
        return posA - posB;
      });
      return [first, ...sortedRest];
    }
    return columnList;
  };
  const formatRowData = (rawData: any[], settings: any) => {
    if (!rawData || !Array.isArray(rawData)) return [];
    const data = JSON.parse(JSON.stringify(rawData));
    return data
      ?.sort((a: any, b: any) => {
        const positionA = a?.indicator?.position ?? 0;
        const positionB = b?.indicator?.position ?? 0;
        return positionA - positionB;
      })
      ?.map((row: any) => {
        return {
          indicator: row.indicator,
          isCollapsable: row.dim_df?.length > 0,
          isCollapsed: true,
          ...row.total[0],
          indicator_id: row.indicator.indicator_id,
          'Comparison (PL)': row.total[0].Comparison,
          Indicators: row.indicator.indicator_name,
          children:
            row.dim_df && row.dim_df.length > 0
              ? row.dim_df
                  .map((r: any) => {
                    const dimensionName =
                      getDimensionNameFromId(
                        row.indicator.dimension_id,
                        row.indicator.block_id,
                        row.indicator.indicator_id,
                      ) || row.indicator.dimension_name;
                    return {
                      ...r,
                      indicator: row.indicator,
                      Indicators: r[dimensionName] || r[`${row.indicator.dimension_name}`],
                      'Comparison (PL)': r.Comparison,
                    };
                  })
                  .map((r: any) => {
                    const formattedRow: any = { ...r };
                    delete formattedRow.dim_df;
                    if (settings?.varience_to_py) {
                      formattedRow['▲PY'] = r.PY;
                      delete formattedRow.PY;
                    }
                    if (settings?.varience_percentage_py) {
                      formattedRow['▲PY%'] = r['PY%'];
                      delete formattedRow['PY%'];
                    }
                    if (settings?.varience_to_pl) {
                      formattedRow['▲PL'] = r.PL;
                      delete formattedRow.PL;
                    }
                    if (settings?.varience_percentage_pl) {
                      formattedRow['▲PL%'] = r['PL%'];
                      delete formattedRow['PL%'];
                    }

                    if (settings?.actual_column_name) {
                      formattedRow[settings?.actual_column_name] = r[settings?.actual_column_name];
                      delete formattedRow.Actual;
                    } else {
                      formattedRow.Actual = r.Actual;
                      delete formattedRow[settings?.actual_column_name];
                    }
                    if (settings?.plan_comparison_column_name) {
                      formattedRow[settings?.plan_comparison_column_name] =
                        r[settings?.plan_comparison_column_name];
                      delete formattedRow['Comparison (PL)'];
                    } else {
                      formattedRow['Comparison (PL)'] = r['Comparison (PL)'];
                      delete formattedRow[settings?.plan_comparison_column_name];
                    }
                    return formattedRow;
                  })
              : [],
        };
      })
      .map((row: any) => {
        const formattedRow: any = { ...row };
        delete formattedRow.dim_df;
        if (settings?.varience_to_py) {
          formattedRow['▲PY'] = row.PY;
          delete formattedRow.PY;
        }
        if (settings?.varience_percentage_py) {
          formattedRow['▲PY%'] = row['PY%'];
          delete formattedRow['PY%'];
        }
        if (settings?.varience_to_pl) {
          formattedRow['▲PL'] = row.PL;
          delete formattedRow.PL;
        }
        if (settings?.varience_percentage_pl) {
          formattedRow['▲PL%'] = row['PL%'];
          delete formattedRow['PL%'];
        }
        return formattedRow;
      });
  };

  useEffect(() => {
    if (columnSettings && output) {
      const extractedColumns = extractColumns(columnSettings);
      setColumns(extractedColumns);
      const formattedRows = formatRowData(output.data, columnSettings);
      setRows(formattedRows);
    }
  }, [columnSettings, output]);

  return (
    <Box borderRadius='10px' overflowY='auto' overflowX='auto' className='scroll'>
      <TableContainer
        overflowY='auto'
        overflowX='auto'
        mb={{ base: 4.75, md: 0 }}
        mt={{ base: 0, md: 0 }}
        borderRadius='10px'
        className='table_scroll indicator-table dashboard-drag-header'
        cursor='grab'
      >
        <Table variant='base'>
          <Thead height={'9'} zIndex={2}>
            <Tr>
              {columns.map((column: string) => (
                <Th
                  key={column}
                  textAlign={'left'}
                  fontFamily={'regular_roboto'}
                  fontSize={'xs'}
                  fontWeight={500}
                  textTransform='none'
                  title={column}
                >
                  {column?.length > 20 ? `${column.slice(0, 20)}...` : column}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row: any, index: number) => (
              <VarianceRow key={index} row={row} columns={columns} />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
export default Variance;
